import React, { useContext, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import Loader from "react-loader-spinner";
import { postRequest } from "../../CustomHttp";
import { UserInformation } from "./MyProfile";
import PlanCost from "./PlanCost";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { setSession } from "../../Session";

const stripePromise = loadStripe(
  'pk_live_51OkUA1FukknjXIe1tOSqM6J6uYLPCnytkNHwmc3e8IGGdCVMnyuL9U9Ul3cddQPX4pwd7Sy92woDihSEBUU1GE2f00oRy6oi1k'
);

function ChangePlanForm({ close }) {
    const stripe = useStripe();
    const elements = useElements();
    const history = useNavigate();

    const { userDetail, setUserDetail } = useContext(UserInformation);

    const [planId, setPlanId] = useState(null);
    const [planCost, setPlanCost] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cardComplete, setCardComplete] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);

    const handleCardChange = (e) => {
        setErrorMsg(e.error ? e.error.message : null);
        setCardComplete(e.complete);
    };

    const getPlanIdAndCost = (id, cost) => {
        setPlanId(id);
        setPlanCost(cost);
    };

    const handleChangePlan = async () => {
        if (!stripe || !elements) return toast.error("Stripe not loaded. Please refresh page.");;
        if (!planId) return toast.error("Please select a plan");
        if (!cardComplete) return setErrorMsg("Please enter valid card details");

        try {
        setLoading(true);
        setErrorMsg(null);

        /* 1️⃣ Create change plan intent */
        const intentRes = await postRequest(
            "/change-plan-intent",
            { package: planId },
            true
        );

        if (!intentRes || intentRes.status === false) {
            setLoading(false);
            return toast.error(intentRes?.message || "Unable to start payment. Please check details");
        }
        const payload = {
            package: planId,
        };

        if (!intentRes.hasTrial && intentRes.clientSecret) {
            const clientSecret = intentRes.clientSecret;
            if (!clientSecret) {
                setLoading(false);
                return toast.error("Payment initialization failed. Please try again");
            }
    
            /* 2️⃣ Confirm card payment */
            const result = await stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                card: elements.getElement(CardElement),
                billing_details: {
                    name: `${userDetail.first_name} ${userDetail.last_name}`,
                    email: userDetail.email,
                },
                },
            });
    
            if (result.error) {
                setLoading(false);
                return setErrorMsg(result.error.message);
            }

            const paymentIntent = result?.paymentIntent || null;
            const paymentMethodId = paymentIntent?.payment_method || null;
            payload.payment_intent = result.paymentIntent.id;
            payload.paymentMethodId = paymentMethodId;
        }

        /* 3️⃣ Finalize change plan */
        const finalizeRes = await postRequest(
            "/change-plan",
            payload,
            true
        );

        if (finalizeRes?.status) { 
            setUserDetail({
                ...userDetail,
                membership: finalizeRes.data,
            });
            toast.success(finalizeRes.message || "Plan updated successfully");
            setSession("userData", null);
            setSession("authData", null);
            close();
            setTimeout(() => {
                toast.success("For security purposes, please log back in to access your account.");
                history("/login");
            }, 500);
        } else {
            toast.error(finalizeRes?.message || "Failed to update plan");
        }

        setLoading(false);
    } catch (err) {
        console.error(err);
        setLoading(false);
        toast.error("Server error. Please try again.");
    }
  };

    return (
        <>
        {loading && (
            <div className="text-center" style={overlayStyle}>
            <div>
                <Loader type="ThreeDots" color="#017EAD" height={100} width={100} />
                <p>Processing... Please do not refresh.</p>
            </div>
            </div>
        )}

        <PlanCost
            shareIdAndCost={getPlanIdAndCost}
            minVal={userDetail.membership?.person ?? 1}
        />

        {planCost && (
            <>
            <div className="my-10">
                <CardElement
                onChange={handleCardChange}
                options={{
                    hidePostalCode: true,
                    style: {
                        base: {
                            fontSize: "16px",
                        },
                    },
                }}
                />
            </div>

            {errorMsg && (
                <div className="alert alert-danger">{errorMsg}</div>
            )}

            <button
                className="btn btn-primary w-100"
                disabled={loading || !cardComplete || userDetail.membership?.person === planId}
                onClick={handleChangePlan}
            >
                {loading ? "Processing..." : "Update Plan"}
            </button>
            </>
        )}
        </>
    );
}

export default function ChangePlan({ close }) {
    return (
        <Elements stripe={stripePromise}>
        <ChangePlanForm close={close} />
        </Elements>
    );
}

const overlayStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
    backgroundColor: "rgba(255,255,255,0.9)",
};
